import React from 'react';
import { 
  Clock, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  Pause, 
  Play, 
  UserX,
  Calendar
} from 'lucide-react';

interface TaskStatusBadgeProps {
  status: string;
  approvalStatus?: 'approved' | 'pending' | 'rejected';
  isOverdue?: boolean;
  className?: string;
  showIcon?: boolean;
}

const TaskStatusBadge: React.FC<TaskStatusBadgeProps> = ({ 
  status, 
  approvalStatus = 'approved', 
  isOverdue = false, 
  className = '',
  showIcon = true
}) => {
  const getStatusConfig = () => {
    // Handle approval status first
    if (approvalStatus === 'pending') {
      return {
        label: 'Pending Approval',
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: <Clock className="w-3 h-3" />,
        priority: 'medium'
      };
    }

    if (approvalStatus === 'rejected') {
      return {
        label: 'Rejected',
        color: 'bg-red-100 text-red-800 border-red-200',
        icon: <XCircle className="w-3 h-3" />,
        priority: 'high'
      };
    }

    // Handle task status for approved tasks
    switch (status.toLowerCase()) {
      case 'unassigned':
        return {
          label: 'Unassigned',
          color: 'bg-gray-100 text-gray-800 border-gray-200',
          icon: <UserX className="w-3 h-3" />,
          priority: 'low'
        };

      case 'due':
        if (isOverdue) {
          return {
            label: 'Overdue',
            color: 'bg-red-100 text-red-800 border-red-200',
            icon: <AlertTriangle className="w-3 h-3" />,
            priority: 'critical'
          };
        }
        return {
          label: 'Due',
          color: 'bg-blue-100 text-blue-800 border-blue-200',
          icon: <Calendar className="w-3 h-3" />,
          priority: 'medium'
        };

      case 'overdue':
        return {
          label: 'Overdue',
          color: 'bg-red-100 text-red-800 border-red-200',
          icon: <AlertTriangle className="w-3 h-3" />,
          priority: 'critical'
        };

      case 'inprogress':
      case 'in-progress':
        return {
          label: 'In Progress',
          color: 'bg-indigo-100 text-indigo-800 border-indigo-200',
          icon: <Play className="w-3 h-3" />,
          priority: 'medium'
        };

      case 'hold':
      case 'on-hold':
        return {
          label: 'On Hold',
          color: 'bg-orange-100 text-orange-800 border-orange-200',
          icon: <Pause className="w-3 h-3" />,
          priority: 'medium'
        };

      case 'pass':
      case 'completed':
      case 'complete':
        return {
          label: 'Completed',
          color: 'bg-green-100 text-green-800 border-green-200',
          icon: <CheckCircle2 className="w-3 h-3" />,
          priority: 'low'
        };

      case 'fail':
      case 'failed':
        return {
          label: 'Failed',
          color: 'bg-red-100 text-red-800 border-red-200',
          icon: <XCircle className="w-3 h-3" />,
          priority: 'high'
        };

      case 'inactive':
        return {
          label: 'Inactive',
          color: 'bg-gray-100 text-gray-500 border-gray-200',
          icon: <XCircle className="w-3 h-3" />,
          priority: 'low'
        };

      default:
        return {
          label: status.charAt(0).toUpperCase() + status.slice(1),
          color: 'bg-gray-100 text-gray-800 border-gray-200',
          icon: <Clock className="w-3 h-3" />,
          priority: 'low'
        };
    }
  };

  const config = getStatusConfig();

  return (
    <span 
      className={`
        inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border
        ${config.color}
        ${className}
      `}
      title={`Status: ${config.label}${approvalStatus !== 'approved' ? ` (${approvalStatus})` : ''}`}
    >
      {showIcon && config.icon}
      {config.label}
    </span>
  );
};

export default TaskStatusBadge;
